import { TeamType } from "@/types/user";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Code, Palette, Target, TrendingUp, Megaphone, Users } from "lucide-react";

type FunctionalAreaStepProps = {
  selectedArea: TeamType;
  setSelectedArea: (area: TeamType) => void;
};

// Functional areas for solo users (not real teams)
const FUNCTIONAL_AREAS = [
  {
    id: "engineering" as TeamType,
    name: "Engineering",
    description: "Software development, DevOps, technical roles",
    icon: <Code className="h-5 w-5" />
  },
  {
    id: "design" as TeamType,
    name: "Design",
    description: "UI/UX, Graphics, Product Design, Creative",
    icon: <Palette className="h-5 w-5" />
  },
  {
    id: "product" as TeamType,
    name: "Product",
    description: "Product Management, Strategy, Analytics",
    icon: <Target className="h-5 w-5" />
  },
  {
    id: "sales" as TeamType,
    name: "Sales",
    description: "Business Development, Customer Success, Account Management",
    icon: <TrendingUp className="h-5 w-5" />
  },
  {
    id: "general" as TeamType,
    name: "Other",
    description: "Operations, HR, Finance, Marketing, or other roles",
    icon: <Users className="h-5 w-5" />
  }
];

const FunctionalAreaStep = ({ selectedArea, setSelectedArea }: FunctionalAreaStepProps) => {
  return (
    <div className="space-y-4">
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold">What's your functional area?</h3>
        <p className="text-sm text-muted-foreground">
          This helps us provide relevant skills, insights, and content for your role.
          You won't be added to any teams.
        </p>
      </div>
      
      <RadioGroup 
        value={selectedArea} 
        onValueChange={(value) => setSelectedArea(value as TeamType)} 
        className="space-y-3"
      >
        {FUNCTIONAL_AREAS.map((area) => (
          <div 
            key={area.id}
            className="flex items-center space-x-3 border p-4 rounded-md hover:bg-muted/50 cursor-pointer"
          >
            <RadioGroupItem value={area.id} id={area.id} />
            <div className="flex items-center space-x-3 flex-1">
              <div className="p-2 bg-muted rounded-md">
                {area.icon}
              </div>
              <Label htmlFor={area.id} className="flex-1 cursor-pointer">
                <div className="font-medium">{area.name}</div>
                <div className="text-sm text-muted-foreground">{area.description}</div>
              </Label>
            </div>
          </div>
        ))}
      </RadioGroup>
      
      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
        <div className="flex items-start space-x-2">
          <div className="w-1 h-1 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <strong>Solo User:</strong> You'll track your personal growth independently. 
            This functional area selection only helps customize your experience.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FunctionalAreaStep;